import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

export const ROUTE_KEYS = ["safe", "explicit", "copilot", "campaigns", "promo-pack", "briefs"];

export const ROUTE_META = {
  safe: {
    label: "Safe",
    title: "Safe Content AI",
    platforms: "Instagram, TikTok, Bluesky",
  },
  explicit: {
    label: "Explicit",
    title: "Explicit Content AI",
    platforms: "X, OnlyFans, JustForFans, JFF Inner Circle, RFC, Faphouse",
  },
  copilot: { label: "Copilot", title: "AI Copilot", platforms: "CRM conversations and assistance" },
  campaigns: { label: "Campaigns", title: "Campaign generation", platforms: "Campaign drafts and messaging" },
  "promo-pack": { label: "Promo Pack", title: "Promo Pack generation", platforms: "Promotional content bundles" },
  briefs: { label: "Briefs", title: "AI Briefs", platforms: "Daily and scheduled briefs" },
};

export const inputClass =
  "w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-purple-500";
export const labelClass = "block text-xs font-medium text-zinc-400 mb-1";
export const buttonPrimary =
  "px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-sm font-medium transition disabled:opacity-50";
export const buttonGhost =
  "px-3 py-2 rounded-lg border border-zinc-700 hover:bg-zinc-800 text-sm font-medium text-zinc-300 transition disabled:opacity-50";

export function errorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length) return detail.map((d) => d.msg).join("; ");
  return fallback;
}

// Current text providers exposed by the backend. Values mirror its defaults and are
// used only until GET /api/ai/settings has loaded.
export const XAI_PROVIDER = "xai";
export const XAI_LABEL = "xAI Grok";
export const XAI_ENV_KEY = "XAI_API_KEY";
export const XAI_DEFAULT_MODEL = "grok-4.7";
export const XAI_FALLBACK_MODELS = [
  "grok-4.7",
  "grok-4.6",
  "grok-4.5",
  "grok-4.3",
  "grok-4.20-0309-reasoning",
  "grok-4.20-0309-non-reasoning",
];
export const GEMINI_PROVIDER = "gemini";
export const GEMINI_LABEL = "Google Gemini";
export const GEMINI_DEFAULT_MODEL = "gemini-3.8-flash";

/** xAI provider status from the settings payload (or null). */
export function xaiStatus(settings) {
  return settings?.provider_status?.find((p) => p.provider === XAI_PROVIDER) || null;
}

/** Grok models offered in the model picker. */
export function xaiModels(settings) {
  const models = xaiStatus(settings)?.models;
  return Array.isArray(models) && models.length ? models : XAI_FALLBACK_MODELS;
}

export function providerModels(settings, provider) {
  const entry = settings?.provider_status?.find((p) => p.provider === provider);
  return entry?.models?.length ? entry.models : provider === GEMINI_PROVIDER ? [GEMINI_DEFAULT_MODEL] : XAI_FALLBACK_MODELS;
}

/**
 * Display name for a provider id. Older usage logs can still name a previous
 * provider (gemini, openai, ...); those are shown as-is so history stays honest.
 */
export function providerLabel(settings, provider) {
  if (!provider) return "None";
  if (provider === XAI_PROVIDER) return xaiStatus(settings)?.label || XAI_LABEL;
  return settings?.provider_status?.find((p) => p.provider === provider)?.label || provider;
}

/**
 * Loads GET /api/ai/settings (admin only).
 * Pass enabled=false to defer the request, for example until a panel opens.
 */
export function useAISettings({ enabled = true } = {}) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/ai/settings");
      setSettings(data);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, "Failed to load AI settings"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled && !settings && !loading) reload();
  }, [enabled, settings, loading, reload]);

  return { settings, loading, error, reload };
}
